/**
 * Groups — the named lists a campaign can be sent to.
 *
 * The tables, the API and the campaign composer's picker all existed before
 * this screen did, which meant the picker was permanently empty and looked
 * broken rather than unused. This is the missing half: somewhere to make a
 * group and see how many people are in it.
 *
 * A row opens the group: name, both counts, and its members. The counts are
 * two because they answer different questions — how many people are in the
 * group, and how many of them a campaign would actually reach.
 */

import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { Icon } from "../components/Sprites";
import { useSession } from "../session";
import type { CursorPage, Group } from "../api/types";

const PAGE_SIZE = 50;

export function Groups() {
  const { orgId, can } = useSession();
  const navigate = useNavigate();
  const writable = can("contacts:write");

  const [history, setHistory] = useState<string[]>([]);
  const cursor = history.at(-1) ?? "";

  const { data, error, loading, reload } = useAsync<CursorPage<Group>>(
    (signal) =>
      api.get<CursorPage<Group>>("/v1/groups", { signal, query: { limit: PAGE_SIZE, cursor } }),
    [orgId, cursor],
  );

  const rows = data?.data ?? [];

  return (
    <>
      <div className="topbar">
        <h1 className="oc-headline-lg">Groups</h1>
        <div className="row">
          {writable && <CreateGroup onCreated={reload} />}
          <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={reload}>
            <Icon name="refresh" size={12} />
            Refresh
          </button>
        </div>
      </div>

      <div className="content">
        <div className="oc-panel oc-starfield" style={{ flex: 1, minWidth: 0 }}>
          <div className="oc-table groups-table" style={{ flex: 1, minHeight: 0 }}>
            <div className="oc-table__head" role="row">
              <span>Name</span>
              <span className="oc-table__cell--right">Members</span>
              <span className="oc-table__cell--right">Sendable</span>
              <span className="oc-table__cell--right">Created</span>
              <span className="oc-table__cell--right">Action</span>
            </div>

            <div className="oc-table__body">
              {error && (
                <p className="field-error" style={{ padding: "var(--oc-gutter)" }}>
                  {error.message}
                </p>
              )}

              {loading && rows.length === 0 && <SkeletonRows columns={4} />}

              {!error && rows.length === 0 && !loading && (
                <div className="oc-empty">
                  <div className="oc-empty__title">No groups yet</div>
                  <div className="oc-empty__hint">
                    A group is a named list of contacts. Make one, add people to it from the
                    Contacts screen, and a campaign can then be sent to it — or to several
                    at once, which reaches everyone in any of them without sending twice
                    to anybody in more than one.
                  </div>
                </div>
              )}

              {rows.map((group) => (
                <Row
                  key={group.id}
                  group={group}
                  removable={writable}
                  onRemoved={reload}
                  onOpen={() => navigate(`/groups/${group.id}`)}
                />
              ))}
            </div>

            <div className="oc-table__footer">
              <span className="oc-mono-sm">
                {rows.length === 0 ? "No groups" : `${rows.length} shown`}
              </span>
              <span style={{ display: "flex", gap: "var(--oc-space-2)" }}>
                <button
                  type="button"
                  className="oc-btn oc-btn--ghost oc-btn--sm"
                  disabled={history.length === 0}
                  onClick={() => setHistory((h) => h.slice(0, -1))}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className="oc-btn oc-btn--ghost oc-btn--sm"
                  disabled={!data?.has_more || !data.next_cursor}
                  onClick={() => {
                    if (data?.next_cursor) setHistory((h) => [...h, data.next_cursor!]);
                  }}
                >
                  Next
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Row({
  group,
  removable,
  onRemoved,
  onOpen,
}: {
  group: Group;
  removable: boolean;
  onRemoved: () => void;
  onOpen: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    if (
      !window.confirm(
        `Delete "${group.name}"?\n\n` +
          "The contacts stay — only the grouping goes. Any campaign still " +
          "targeting this group will resolve to a smaller audience, or to " +
          "everyone if it was the only group selected.",
      )
    ) {
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.delete(`/v1/groups/${encodeURIComponent(group.id)}`);
      onRemoved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the group.");
      setBusy(false);
    }
  };

  return (
    <div
      className="oc-table__row"
      role="row"
      tabIndex={0}
      style={{ cursor: "pointer" }}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <span className="oc-body-sm oc-truncate">
        {group.name}
        {group.description && (
          <span className="oc-text-secondary"> — {group.description}</span>
        )}
        {error && (
          <span className="field-error" role="alert">
            {error}
          </span>
        )}
      </span>

      {/* Two numbers, because they answer different questions. Members is how
          many people are in the group; sendable is how many a campaign would
          actually reach. A list of five hundred where two hundred opted out is
          a different thing from a list of three hundred, and only showing the
          first number is how somebody plans a send against a figure that is
          not true. */}
      <span className="oc-table__cell--right oc-mono-sm">
        {group.member_count.toLocaleString()}
      </span>
      <span className="oc-table__cell--right oc-mono-sm">
        {group.sendable_count != null ? (
          <span className={group.sendable_count < group.member_count ? "oc-text-secondary" : undefined}>
            {group.sendable_count.toLocaleString()}
          </span>
        ) : (
          <span className="oc-text-muted">—</span>
        )}
      </span>

      <span className="oc-table__cell--right oc-mono-sm oc-text-secondary">
        {new Date(group.created_at).toLocaleDateString()}
      </span>

      <span className="oc-table__cell--right">
        {removable && (
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--sm"
            disabled={busy}
            // Stopped, or deleting a group also navigates into the one being
            // deleted — the row is the click target now.
            onClick={(event) => {
              event.stopPropagation();
              void remove();
            }}
          >
            {busy ? "Deleting…" : "Delete"}
          </button>
        )}
      </span>
    </div>
  );
}

/** The create control, opened inline rather than in a dialog. */
function CreateGroup({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/v1/groups", { name: name.trim() });
      setName("");
      setOpen(false);
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the group.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="oc-btn oc-btn--primary" onClick={() => setOpen(true)}>
        <Icon name="plus" size={12} />
        New group
      </button>
    );
  }

  return (
    <form className="row" style={{ gap: "var(--oc-space-2)" }} onSubmit={(e) => void submit(e)}>
      <input
        className="oc-input"
        style={{ width: 240 }}
        autoFocus
        required
        maxLength={200}
        placeholder="Newsletter subscribers"
        value={name}
        onChange={(event) => setName(event.target.value)}
        aria-label="Group name"
        aria-invalid={error ? true : undefined}
      />
      <button
        type="submit"
        className="oc-btn oc-btn--primary oc-btn--sm"
        disabled={busy || name.trim() === ""}
      >
        {busy ? "Creating…" : "Create"}
      </button>
      <button
        type="button"
        className="oc-btn oc-btn--ghost oc-btn--sm"
        onClick={() => {
          setOpen(false);
          setError(null);
        }}
      >
        Cancel
      </button>
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </form>
  );
}
